// RecommendationService.js
import { getFirestoreInstance } from './firebaseHelper';
import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { sendRecommendationRequestNotification } from './NotificationService';

// Cache keys for recommendation requests
const RECOMMENDATION_REQUEST_CACHE_PREFIX = 'recommendation_request_';
const CACHE_EXPIRY_DAYS = 30;

/**
 * Save recommendation request data to cache
 * @param {string} currentUserId - Current user's ID
 * @param {string} targetUserId - Target user's ID
 * @param {Object} requestData - Request data with timestamp
 */
const saveRequestToCache = (currentUserId, targetUserId, requestData) => {
  try {
    const cacheKey = `${RECOMMENDATION_REQUEST_CACHE_PREFIX}${currentUserId}_${targetUserId}`;
    const cacheData = {
      ...requestData,
      cacheTimestamp: Date.now(),
      cacheExpiry: Date.now() + (CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000) // 30 days in milliseconds
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error saving recommendation request to cache:', error);
  }
};

/**
 * Get recommendation request data from cache
 * @param {string} currentUserId - Current user's ID
 * @param {string} targetUserId - Target user's ID
 * @returns {Object|null} Request data or null if not found or expired
 */
const getRequestFromCache = (currentUserId, targetUserId) => {
  try {
    const cacheKey = `${RECOMMENDATION_REQUEST_CACHE_PREFIX}${currentUserId}_${targetUserId}`;
    const cachedData = localStorage.getItem(cacheKey);
    
    if (!cachedData) return null;
    
    const parsedData = JSON.parse(cachedData);
    
    // Check if cache has expired
    if (parsedData.cacheExpiry && parsedData.cacheExpiry < Date.now()) {
      // Cache expired, remove it
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    return parsedData;
  } catch (error) {
    console.error('Error getting recommendation request from cache:', error);
    return null;
  }
};

/**
 * Check if a recommendation request is in cooldown period
 * @param {string} currentUserId - Current user's ID
 * @param {string} targetUserId - Target user's ID
 * @returns {Promise<{inCooldown: boolean, cooldownEndDate: Date|null}>} Cooldown status and end date
 */
export const checkRecommendationCooldown = async (currentUserId, targetUserId) => {
  try {
    // First check cache
    const cachedRequest = getRequestFromCache(currentUserId, targetUserId);
    
    if (cachedRequest) {
      const requestTimestamp = cachedRequest.timestamp?.seconds 
        ? new Date(cachedRequest.timestamp.seconds * 1000)
        : new Date(cachedRequest.timestamp);
      
      const cooldownEndDate = new Date(requestTimestamp);
      cooldownEndDate.setDate(cooldownEndDate.getDate() + CACHE_EXPIRY_DAYS);
      
      return {
        inCooldown: cooldownEndDate > new Date(),
        cooldownEndDate: cooldownEndDate > new Date() ? cooldownEndDate : null,
        fromCache: true
      };
    }
    
    // If not in cache, check Firestore
    const db = getFirestoreInstance();
    const requestRef = doc(db, 'users', currentUserId, 'sentRecommendationRequests', targetUserId);
    const requestDoc = await getDoc(requestRef);
    
    if (requestDoc.exists()) {
      const requestData = requestDoc.data();
      const requestTimestamp = requestData.timestamp?.toDate() || new Date();
      
      const cooldownEndDate = new Date(requestTimestamp);
      cooldownEndDate.setDate(cooldownEndDate.getDate() + CACHE_EXPIRY_DAYS);
      
      // Cache the result for future checks
      saveRequestToCache(currentUserId, targetUserId, requestData);
      
      return {
        inCooldown: cooldownEndDate > new Date(),
        cooldownEndDate: cooldownEndDate > new Date() ? cooldownEndDate : null,
        fromCache: false
      };
    }
    
    // No request found
    return {
      inCooldown: false,
      cooldownEndDate: null,
      fromCache: false
    };
  } catch (error) {
    console.error('Error checking recommendation cooldown:', error);
    return {
      inCooldown: false,
      cooldownEndDate: null,
      error: error.message
    };
  }
};

/**
 * Send a recommendation request and update tracking
 * @param {string} currentUserId - Current user's ID
 * @param {Object} currentUserData - Current user's data (name, photo, etc.)
 * @param {string} targetUserId - Target user's ID
 * @returns {Promise<boolean>} Success status
 */
export const sendRecommendationRequest = async (currentUserId, currentUserData, targetUserId) => {
  try {
    if (!currentUserId || !targetUserId || !currentUserData) {
      throw new Error('Missing required data');
    }
    
    // Check cooldown first
    const cooldownStatus = await checkRecommendationCooldown(currentUserId, targetUserId);
    
    if (cooldownStatus.inCooldown) {
      const daysLeft = Math.ceil((cooldownStatus.cooldownEndDate - new Date()) / (1000 * 60 * 60 * 24));
      throw new Error(`Please wait ${daysLeft} more days before sending another request to this user.`);
    }
    
    // Prepare minimal sender data for notification
    const senderData = {
      userId: currentUserId,
      userName: currentUserData.userName,
      fullName: currentUserData.fullName,
      profilePictureUrl: currentUserData.profilePictureUrl || ''
    };
    
    // Store the request in the current user's sentRecommendationRequests subcollection
    const db = getFirestoreInstance();
    const requestRef = doc(db, 'users', currentUserId, 'sentRecommendationRequests', targetUserId);
    
    const requestData = {
      targetUserId: targetUserId,
      timestamp: serverTimestamp(),
      status: 'pending'
    };
    
    // Save to Firestore
    await setDoc(requestRef, requestData);
    
    // Also cache it locally with current timestamp (since serverTimestamp is null on client)
    const cacheData = {
      ...requestData,
      timestamp: new Date().toISOString()
    };
    saveRequestToCache(currentUserId, targetUserId, cacheData);
    
    // Send the notification to the target user
    const success = await sendRecommendationRequestNotification(targetUserId, senderData);
    
    return success;
  } catch (error) {
    console.error('Error sending recommendation request:', error);
    throw error;
  }
};

/**
 * Get formatted time remaining for cooldown
 * @param {Date} endDate - Cooldown end date
 * @returns {string} Formatted time (e.g., "5 days", "2 hours")
 */
export const formatCooldownTimeRemaining = (endDate) => {
  if (!endDate) return 'None';
  
  const now = new Date();
  const diffMs = endDate - now;
  
  if (diffMs <= 0) return 'None';
  
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  if (diffDays > 0) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  } else {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
  }
};

/**
 * Clear expired cache entries for recommendation requests
 */
export const cleanupExpiredRequestCache = () => {
  try {
    const now = Date.now();
    
    // Get all localStorage keys
    Object.keys(localStorage)
      .filter(key => key.startsWith(RECOMMENDATION_REQUEST_CACHE_PREFIX))
      .forEach(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          
          if (data.cacheExpiry && data.cacheExpiry < now) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          // If we can't parse the data, it's likely corrupt
          localStorage.removeItem(key);
        }
      });
  } catch (error) {
    console.error('Error cleaning up expired request cache:', error);
  }
};

// Run cleanup on module import
cleanupExpiredRequestCache();